import rawAxios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios'
import { AuthInactivityMonitor } from 'features/auth/AuthInactivityMonitor'
import { GeckoAuthenticationContext } from 'features/auth/geckoAuthContext'
import { attachAccessPreviewMutationGuard } from 'features/permissions/utils/accessPreviewMutationGuard'
import { clearAccessPreviewFromStorage } from 'features/permissions/utils/accessPreviewStorage'
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { AuthState } from './helper'
import { useKeycloak } from './KeycloakProvider'
/**
 * A component which handles Keycloak authentication, providing a GeckoAuthContext
 * to the rest of the app
 * @param children        The rest of the app (this component is a simple wrapper)
 */
export const KeycloakAuthContext: FC<{
  children: ReactNode
}> = ({ children }) => {
  const keycloak = useKeycloak()
  const [validRefreshToken, setValidRefreshToken] = useState<boolean>(true)
  const [authState, setAuthState] = useState(new AuthState())

  // TODO: validate this works in keycloak
  const exchangeRefreshTokenAlias = keycloak.exchangeRefreshToken
  const attemptGetAccessToken = useCallback(() => {
    const tokenExchangePromise = exchangeRefreshTokenAlias()
    if (tokenExchangePromise) {
      tokenExchangePromise.catch(() => {
        setValidRefreshToken(false)
      })
    }
  }, [exchangeRefreshTokenAlias])

  useEffect(() => {
    if (!keycloak.user?.access_token || !keycloak.user.expires_at) {
      throw new Error('Keycloak user requires access_token and expires_at')
    }
    setValidRefreshToken(true)
    setAuthState({
      authorization: `Bearer ${keycloak.user.access_token}`,
      tokenExpiration: new Date(keycloak.user.expires_at * 1000),
    })
  }, [keycloak, keycloak.user])

  const authorizedAxios = useMemo(() => {
    const newAxios = rawAxios.create()
    attachAccessPreviewMutationGuard(newAxios)
    newAxios.interceptors.request.use((requestConfig: AxiosRequestConfig) => {
      return {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: authState.authorization ?? null,
        } as AxiosRequestHeaders,
      }
    })
    return newAxios
  }, [authState.authorization])

  const logout = React.useCallback(() => {
    clearAccessPreviewFromStorage()
    keycloak.signOut()
  }, [keycloak])

  return (
    <>
      <AuthInactivityMonitor
        tokenExpiration={authState.tokenExpiration}
        validRefreshToken={validRefreshToken}
        attemptGetAccessToken={attemptGetAccessToken}
        redirectToLogin={() => keycloak.sendToKeycloakLogin()}
      />
      <GeckoAuthenticationContext.Provider
        value={{
          authorization: authState.authorization,
          authorizedAxios,
          isAuthenticated: !!authState.authorization,
          logout: logout,
        }}>
        {children}
      </GeckoAuthenticationContext.Provider>
    </>
  )
}
